// Типизированный клиент API сделок и справочников (T2.1).
import { apiGet, apiPost, getToken } from './api';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';
async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`${method} ${path} → ${res.status}`);
  return res.json() as Promise<T>;
}

export type DealListItem = {
  id: number;
  no: string;
  client: string;
  region: string | null;
  stage: number;
  sum: number;
  paid: number;
  stage_name: string;
  otk: number;
  archived_at: string | null;
};
export type DealsList = { stages: string[]; deals: DealListItem[]; archivedCount: number };

export type Item = {
  id: number;
  catalog_id: number | null;
  name: string;
  qty: number;
  price: number;
  cat: string | null;
  ceh_id: string | null;
  prog: number;
};
export type Pay = {
  id: number;
  seq: number;
  label: string;
  pct: number;
  amount: number;
  due_offset: number;
  due_date: string | null;
  paid: number;
  paid_at: string | null;
  paid_amount: number;
  paid_ref: string | null;
};
export type Flag = { level: string; text: string };
export type Threads = { pay: string; supply: string; prod: string; ship: string };
export type ShipItem = { id: number; name: string; qty_total: number; qty_shipped: number };

export type DealFull = {
  id: number;
  no: string;
  client: string;
  region: string | null;
  le: string | null;
  manager: string | null;
  stage: number;
  sum: number;
  kp: number;
  contract_no: string | null;
  contract_signed: number;
  contract_date: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  otk: number;
  note: string | null;
  archived_at: string | null;
  sched_start_date: string | null;
  sched_ship_date: string | null;
  items: Item[];
  pay: Pay[];
  ceh: Record<string, number>;
  ship: { trucks: number; kpp: number; started: number; items: ShipItem[] } | null;
  computed: {
    paidSum: number;
    dueSum: number;
    paidPct: number;
    prodPct: number;
    shipPct: number;
    stageName: string;
    stageCount: number;
    productionIndex: number;
  };
  flags: Flag[];
  threads: Threads;
  financeHidden?: boolean;
};

export type CatalogItem = {
  id: number;
  cat: string;
  name: string;
  unit: string;
  price: number;
  ceh_id: string;
  ico: string;
};
export type Template = { id: number; name: string; descr: string };

export const listDeals = (archived = false) =>
  apiGet<DealsList>('/api/deals' + (archived ? '?archived=1' : ''));
export const getDeal = (id: number) => apiGet<DealFull>(`/api/deals/${id}`);
export const archiveDeal = (id: number) => apiPost<{ ok: boolean }>(`/api/deals/${id}/archive`);
export const restoreDeal = (id: number) => apiPost<{ ok: boolean }>(`/api/deals/${id}/restore`);
export const createDeal = (client: string, region?: string) =>
  apiPost<DealFull>('/api/deals', { client, region });
// fromStage — этап, который клиент видел (оптимистичная блокировка перемещения).
export const advanceDeal = (id: number, fromStage?: number) =>
  apiPost<DealFull>(
    `/api/deals/${id}/advance`,
    fromStage === undefined ? undefined : { from_stage: fromStage },
  );
export const setStage = (id: number, stage: number, fromStage?: number) =>
  apiPost<DealFull>(`/api/deals/${id}/stage`, {
    stage,
    ...(fromStage === undefined ? {} : { from_stage: fromStage }),
  });

// Перемещение этапа с разбором ответа: успех / блокировка оплатой / конфликт версии.
export type StageBlock = {
  error: 'payment_required' | 'overdue_payment';
  message: string;
  advance?: { pay_id: number; amount: number; label: string } | null;
};
export type StageMoveResult =
  | { status: 'ok'; deal: DealFull }
  | { status: 'blocked'; block: StageBlock }
  | { status: 'conflict' };

async function stageReq(path: string, body: unknown): Promise<StageMoveResult> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (res.ok) return { status: 'ok', deal: (await res.json()) as DealFull };
  const j = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
  if (res.status === 409 && (j.error === 'payment_required' || j.error === 'overdue_payment'))
    return { status: 'blocked', block: j as StageBlock };
  if (res.status === 409 && j.error === 'conflict') return { status: 'conflict' };
  throw new Error(j.message ?? `stage → ${res.status}`);
}
export const moveStageChecked = (id: number, stage: number, fromStage?: number) =>
  stageReq(`/api/deals/${id}/stage`, {
    stage,
    ...(fromStage === undefined ? {} : { from_stage: fromStage }),
  });
export const advanceChecked = (id: number, fromStage?: number) =>
  stageReq(`/api/deals/${id}/advance`, fromStage === undefined ? {} : { from_stage: fromStage });

// ---- Настройка этапов воронки ----
export type Stage = { seq: number; name: string; is_production: number };
export const listStages = () => apiGet<{ stages: Stage[] }>('/api/stages');
export const renameStage = (seq: number, name: string) =>
  req('PATCH', `/api/stages/${seq}`, { name });
export const addStage = (name: string) => apiPost('/api/stages', { name });
export const delStage = (seq: number) => req('DELETE', `/api/stages/${seq}`);
export const addItem = (id: number, catalog_id: number) =>
  apiPost<DealFull>(`/api/deals/${id}/items`, { catalog_id });
export const applyTemplate = (id: number, template_id: number) =>
  apiPost<DealFull>(`/api/deals/${id}/apply-template`, { template_id });
// Отметка оплаты: { paid:false } — откат; { amount, ref } — частичная/полная; {} — полная.
export const markPay = (
  id: number,
  payId: number,
  body: { paid?: boolean; amount?: number; ref?: string },
) => apiPost<DealFull>(`/api/deals/${id}/pay/${payId}/mark`, body);
export const applyPreset = (id: number, key: string) =>
  apiPost<DealFull>(`/api/deals/${id}/pay/preset`, { key });

// Применить пресет с обработкой «в плане есть оплаченные транши» (нужен force).
export async function applyPresetChecked(
  id: number,
  key: string,
  force = false,
): Promise<{ status: 'ok'; deal: DealFull } | { status: 'needs_force' }> {
  const res = await fetch(`${API_BASE}/api/deals/${id}/pay/preset`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
    },
    body: JSON.stringify({ key, force }),
  });
  if (res.status === 409) {
    const j = (await res.json().catch(() => ({}))) as { error?: string };
    if (j.error === 'has_paid') return { status: 'needs_force' };
  }
  if (!res.ok) throw new Error(`preset → ${res.status}`);
  return { status: 'ok', deal: (await res.json()) as DealFull };
}

// Настраиваемые пресеты плана платежей.
export type PayPresetItem = { seq: number; label: string; pct: number; due_offset: number };
export type PayPreset = { id: number; key: string; name: string; items: PayPresetItem[] };
export const listPayPresets = () => apiGet<{ presets: PayPreset[] }>('/api/pay/presets');
export const addPay = (id: number) => apiPost<DealFull>(`/api/deals/${id}/pay`);
export const delPay = (id: number, payId: number) =>
  req<DealFull>('DELETE', `/api/deals/${id}/pay/${payId}`);
export const patchPay = (
  id: number,
  payId: number,
  patch: { label?: string; pct?: number; due_offset?: number },
) => req<DealFull>('PATCH', `/api/deals/${id}/pay/${payId}`, patch);
export const patchDeal = (
  id: number,
  patch: {
    note?: string;
    contact_name?: string;
    contact_phone?: string;
    sched_start_date?: string | null;
    sched_ship_date?: string | null;
  },
) => req<DealFull>('PATCH', `/api/deals/${id}`, patch);

export const getCatalog = () => apiGet<{ catalog: CatalogItem[] }>('/api/ref/catalog');
export const getTemplates = () => apiGet<{ templates: Template[] }>('/api/ref/templates');

export const patchItem = (id: number, itemId: number, delta: number) =>
  req<DealFull>('PATCH', `/api/deals/${id}/items/${itemId}`, { delta });
export const deleteItem = (id: number, itemId: number) =>
  req<DealFull>('DELETE', `/api/deals/${id}/items/${itemId}`);

// ---- Документы ----
export type DocKind = 'kp' | 'contract' | 'invoice' | 'waybill';
export type DocRow = { id: number; kind: DocKind; no: string; amount: number; created_at: string };
export const genDoc = (dealId: number, kind: DocKind) =>
  apiPost<{ doc: DocRow; html: string }>(`/api/deals/${dealId}/docs/${kind}`);
export const listDocs = (dealId: number) =>
  apiGet<{ docs: DocRow[]; titles: Record<string, string> }>(`/api/deals/${dealId}/docs`);
export async function fetchDocHtml(dealId: number, docId: number): Promise<string> {
  const res = await fetch(`${API_BASE}/api/deals/${dealId}/docs/${docId}/html`, {
    headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {},
  });
  return res.text();
}
export function openDocWindow(html: string) {
  const w = window.open('', '_blank');
  if (w) {
    w.document.open();
    w.document.write(html);
    w.document.close();
  }
}
